// The one place that talks to the API.
//
// Every request goes through here so the base URL, the bearer token, and error
// handling live in a single file. Nothing else in the app calls `fetch`.

import { isErrorResponse, type ErrorCode } from "@historia/shared/contracts";

/**
 * Where the API lives. Vite inlines this at build time, so it's a Pages
 * environment variable in production and .env locally.
 *
 * Failing loudly here beats a page full of mysterious network errors — the same
 * approach the old lib/supabase/env.ts took.
 */
const BASE_URL = import.meta.env.VITE_API_URL;

if (!BASE_URL) {
  throw new Error(
    "VITE_API_URL não está definido. Copia .env.example para .env (ou define a variável no Cloudflare Pages).",
  );
}

/** An error the API returned, with its code and Portuguese message intact. */
export class ApiError extends Error {
  readonly code: ErrorCode;
  readonly field?: string;
  readonly status: number;

  constructor(code: ErrorCode, message: string, status: number, field?: string) {
    super(message);
    this.name = "ApiError";
    this.code = code;
    this.field = field;
    this.status = status;
  }
}

// ------------------------------------------------------------
//  Session token
//
//  localStorage, not a cookie. The threat model is two people on their own
//  devices, with no third-party content and no user-generated HTML, so the
//  XSS-exfiltration argument for httpOnly has little purchase here. Against
//  that, a cookie would need SameSite=None across historia.* → api.historia.*
//  and would be exactly the cookie-session model STACK.md §2.2 rules out for
//  future native apps. localStorage also transfers to Capacitor unchanged.
//
// ------------------------------------------------------------

const TOKEN_KEY = "historia.token";

export function readToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    // Safari in private mode throws on localStorage access.
    return null;
  }
}

export function saveToken(token: string): void {
  try {
    localStorage.setItem(TOKEN_KEY, token);
  } catch {
    /* nothing sensible to do */
  }
}

export function clearToken(): void {
  try {
    localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* nothing sensible to do */
  }
}

// ------------------------------------------------------------
//  Requests
// ------------------------------------------------------------

/** Called when any request comes back 401, so the app can send you to /login. */
let onUnauthenticated: (() => void) | null = null;

export function setUnauthenticatedHandler(handler: () => void): void {
  onUnauthenticated = handler;
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  signal?: AbortSignal;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = "GET", body, signal } = options;

  /**
   * `/me` is the request that ASKS whether there is a session. A 401 from it is
   * the answer "not signed in" — not a session that just expired — so it must
   * not fire the global handler.
   *
   * Without this the app deadlocks on first load: 401 → the handler clears the
   * query cache → clearing refetches `/me` → 401 → … The session never resolves
   * and the splash screen never goes away.
   */
  const isSessionProbe = path === "/me";

  const headers: Record<string, string> = {};
  if (body !== undefined) headers["Content-Type"] = "application/json";

  const token = readToken();
  if (token) headers.Authorization = `Bearer ${token}`;

  let response: Response;
  try {
    response = await fetch(`${BASE_URL}/api/v1${path}`, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      signal,
    });
  } catch (error) {
    // A network failure is not an API error — say so plainly rather than
    // surfacing "Failed to fetch".
    if ((error as Error)?.name === "AbortError") throw error;
    throw new ApiError(
      "INTERNAL",
      "Não foi possível falar com o servidor. Verifica a ligação.",
      0,
    );
  }

  if (response.status === 204) return undefined as T;

  const raw = await response.text();
  const payload: unknown = raw ? JSON.parse(raw) : null;

  if (!response.ok) {
    if (isErrorResponse(payload)) {
      if (payload.erro.code === "UNAUTHENTICATED" && !isSessionProbe) onUnauthenticated?.();
      throw new ApiError(
        payload.erro.code,
        payload.erro.message,
        response.status,
        payload.erro.field,
      );
    }
    // Shouldn't happen — the API always uses the envelope — but never surface
    // raw HTML or an empty body to the person using the app.
    throw new ApiError("INTERNAL", "Algo correu mal. Tenta novamente.", response.status);
  }

  return payload as T;
}

// ------------------------------------------------------------
//  Auth (Better Auth's own routes, outside /api/v1)
// ------------------------------------------------------------

/**
 * Signs in and stores the bearer token.
 *
 * The token comes back in the `set-auth-token` response header (the bearer
 * plugin), not the body — which is why the API has to expose that header in
 * its CORS config, or the browser would hide it.
 */
export async function signIn(email: string, password: string): Promise<void> {
  let response: Response;
  try {
    response = await fetch(`${BASE_URL}/api/auth/sign-in/email`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    });
  } catch {
    throw new ApiError("INTERNAL", "Não foi possível falar com o servidor. Verifica a ligação.", 0);
  }

  if (!response.ok) {
    // Better Auth's messages are English and leak whether an account exists;
    // one message for every failure, in Portuguese, tells an attacker nothing
    // and reads better for the two people who actually use this.
    throw new ApiError("UNAUTHENTICATED", "Email ou palavra-passe incorretos.", response.status);
  }

  const token = response.headers.get("set-auth-token");
  if (!token) {
    throw new ApiError("INTERNAL", "A sessão não foi criada. Tenta novamente.", response.status);
  }

  saveToken(token);
}

/**
 * Signs out server-side, then drops the local token regardless of the outcome.
 *
 * The empty `{}` body is required, not decoration: sign-out sends
 * Content-Type: application/json, and Better Auth rejects that with 400
 * "Invalid JSON in request body" when the body is empty — which leaves the
 * session alive in the database while the client believes it signed out.
 */
export async function signOut(): Promise<void> {
  const token = readToken();
  if (token) {
    await fetch(`${BASE_URL}/api/auth/sign-out`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: "{}",
    }).catch(() => {
      // A failed sign-out must not strand someone in a signed-in UI.
    });
  }
  clearToken();
}

export const api = {
  get: <T>(path: string, signal?: AbortSignal) => request<T>(path, { signal }),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: "POST", body }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: "PATCH", body }),
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: "PUT", body }),
  delete: <T>(path: string, body?: unknown) => request<T>(path, { method: "DELETE", body }),
};

/** The raw base URL, for the upload flow's direct-to-R2 PUT. */
export const apiBaseUrl = BASE_URL;
